const { getElement, getElements, debounce, viewportSize } = window.BAO.Utils

export class ProductMedia extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['media', 'dot', 'link']
  }

  connectedCallback () {
    super.connectedCallback()
    this.currentProductUrl = this.externalEls?.imageswap?.element?.dataset?.productUrl
  }

  setupListeners () {
    super.setupListeners()

    if (this.els?.media?.exists && this.els?.dot?.exists) {
      this.handleMediaEntry = this.handleMediaEntry.bind(this)
      this.debouncedHandleMediaEntry = debounce(this.handleMediaEntry, 250)

      // eslint-disable-next-line compat/compat
      this.observer = new IntersectionObserver(this.handleMediaEntry, {
        threshold: this.intersectionThreshold,
      })

      this.els.media.elements.forEach(dotEl => {
        this.observer.observe(dotEl)
      })
    }

    if (this.els?.dot?.exists) {
      this.handleDotClick = this.handleDotClick.bind(this)
      this.els.dot.elements.forEach(dotEl =>
        this.listeners.add(dotEl, 'click', this.handleDotClick),
      )
    }

    /*
      CS: Only commented out as this may still be used once Stuart has thought of a different idea.
      After uncommenting, add isTouchDevice back to line 6 window.BAO.Utils
    */

    /*
    if (
      this.externalEls?.imageswap?.exists &&
      this.els?.link?.exists &&
      !isTouchDevice()
    ) {
      this.handleImageSwap = this.handleImageSwap.bind(this)
      this.externalEls.imageswap.elements.forEach(imageSwapEl => {
        this.listeners.add(imageSwapEl, 'mouseenter', this.handleImageSwap)
        this.listeners.add(imageSwapEl, 'focusin', this.handleImageSwap)
      })
    }
    */
  }

  /** @type {IntersectionObserverCallback} */
  handleMediaEntry (entries, observer) {
    if (viewportSize() === 'xs') return

    const entry = entries.find(
      entry => entry.intersectionRatio >= observer.thresholds[0],
    )
    if (!entry) return

    const currentIndex = entry?.target?.dataset?.index
    this.els.dot.elements.forEach(dotEl =>
      dotEl.setAttribute(
        'aria-current',
        String(dotEl.dataset?.index === currentIndex),
      ),
    )
  }

  handleDotClick (event) {
    const currentIndex = event?.target?.dataset?.index
    const currentMedia = this.els.media.elements.find(
      mediaEl => mediaEl.dataset?.index === currentIndex,
    )
    if (!currentMedia) return
    this.els.dot.elements.forEach(dotEl =>
      dotEl.setAttribute(
        'aria-current',
        String(dotEl.dataset?.index === currentIndex),
      ),
    )
    if (viewportSize() !== 'xs') {
      currentMedia.scrollIntoView(true)
    }
    event.target.blur()
  }

  async handleImageSwap (event) {
    const imageSwapEl = event.target.closest(
      this.externalEls?.imageswap?.selector,
    )
    if (
      !imageSwapEl ||
      !imageSwapEl.dataset?.productUrl ||
      imageSwapEl.dataset.productUrl === this.currentProductUrl
    ) {
      return
    }
    this.currentProductUrl = imageSwapEl.dataset.productUrl
    this.els.media.element.setAttribute('aria-busy', 'true')
    const newSlideLinkEl = await this.fetchSlideLinkHtml(
      imageSwapEl.dataset.productUrl,
    )

    /* Delay the render to allow for previous image to fade out slightly */
    setTimeout(() => {
      getElement(this.els.link.selector, { context: this })?.replaceWith(
        newSlideLinkEl,
      )
      /* Fix for ajax rendered blurry images in safari */
      getElements('img[srcset]', { context: this })?.forEach(imageEl => {
        // eslint-disable-next-line no-self-assign
        imageEl.outerHTML = imageEl.outerHTML
      })
      this.els.media.element.setAttribute('aria-busy', 'false')
    }, 400)
  }

  async fetchSlideLinkHtml (productUrl) {
    const response =
      this.storage?.[productUrl] ||
      (await window.BAO.sectionRenderer.fetchSingle(
        'product-media-slide-link',
        productUrl,
      ))
    const newDOM = window.BAO.sectionRenderer.convertToDOM(response)
    this.updateStorage(productUrl, response)
    return getElement(this.els.link.selector, { context: newDOM })
  }

  updateStorage (key, value) {
    const storageObject = Object.create(this.storage)
    storageObject[key] = value
    this.storage = storageObject
  }

  set storage (storageObject) {
    this._storage = storageObject
  }

  get storage () {
    return this._storage || {}
  }

  get intersectionThreshold () {
    /** @type {HTMLElement[]} */
    const mediaElements = this.els.media.elements

    const mediaTallerThanViewport = mediaElements
      .map(media => {
        const { height } = media.getBoundingClientRect()

        return /** @type {const} */ ([media, height])
      })
      .filter(([_, height]) => height > window.innerHeight)

    if (mediaTallerThanViewport.length === 0) {
      return 1
    }

    const [, tallestHeight] = mediaTallerThanViewport.reduce(
      (currentTallest, mediaAndHeight) => {
        if (currentTallest === null) {
          return mediaAndHeight
        }

        const [, height] = mediaAndHeight

        return height > currentTallest[1] ? mediaAndHeight : currentTallest
      },
      null,
    )

    /**
     * The intersection observer still doesn't seem to trigger if we do `window.innerHeight / tallestHeight`,
     * adjusting it by .01 seems to work just fine though
     */
    return window.innerHeight / tallestHeight - 0.01
  }
}

if (!customElements.get('product-media')) {
  customElements.define('product-media', ProductMedia)
}
